import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { IconEnvelope } from '../components/AuthIcons';
import { IconCheck } from '../components/Icons';

export default function WaitingVerify() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const [sent, setSent]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail]   = useState(user?.email || '');
  const [emailError, setEmailError] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const resend = async () => {
    setLoading(true);
    try {
      await api.resendVerify();
      setSent(true);
    } catch (e) {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!newEmail.includes('@')) { setEmailError(t('auth.invalidEmail')); return; }
    setSavingEmail(true);
    try {
      const data = await api.changeEmail(newEmail);
      if (data?.token) localStorage.setItem('sp_token', data.token);
      setEditingEmail(false);
      setSent(true);
      // Refresh user data
      window.location.reload();
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: 'var(--acc2)', letterSpacing: '-0.02em', marginBottom: '6px' }}>
            ScanPilots
          </h1>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', fontWeight: 500 }}>
            {t('auth.tagline')}
          </p>
        </div>

        {/* Karta */}
        <div className="ds-card" style={{ padding: '36px 28px', textAlign: 'center' }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <IconEnvelope size={64} />
          </div>

          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink)', marginBottom: '10px' }}>
            {t('auth.waitingVerifyTitle')}
          </h2>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', lineHeight: '1.7', marginBottom: '16px' }}>
            {t('auth.waitingVerifyDesc')}
          </p>

          {/* Wyświetlony email */}
          {!editingEmail && (
            <div style={{
              padding: '10px 14px',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}>
              <span style={{ fontSize: '12px', color: 'var(--ink)', fontWeight: 500, wordBreak: 'break-all' }}>
                {user?.email}
              </span>
              <button
                onClick={() => { setEditingEmail(true); setSent(false); setEmailError(''); setNewEmail(user?.email || ''); }}
                style={{ background: 'none', border: 'none', color: 'var(--acc)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}
              >
                {t('auth.changeEmail')}
              </button>
            </div>
          )}

          {/* Formularz zmiany emaila */}
          {editingEmail && (
            <form onSubmit={handleChangeEmail} style={{ marginBottom: '20px', textAlign: 'left' }}>
              <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)', display: 'block', marginBottom: '5px' }}>
                {t('auth.newEmailAddress')}
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                className="ds-input"
                style={{ width: '100%', marginBottom: '8px' }}
                autoFocus
                required
              />
              {emailError && (
                <p style={{ fontSize: '11px', color: 'var(--err2)', marginBottom: '8px' }}>{emailError}</p>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="submit"
                  disabled={savingEmail}
                  style={{
                    flex: 1,
                    background: 'var(--acc)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '9px 14px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: savingEmail ? 'default' : 'pointer',
                    opacity: savingEmail ? 0.7 : 1,
                    letterSpacing: '0.04em',
                  }}>
                  {savingEmail ? t('auth.saving') : t('auth.saveEmail')}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingEmail(false); setEmailError(''); }}
                  style={{
                    background: 'var(--bg4)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '9px 14px',
                    fontSize: '11px',
                    color: 'var(--ink3)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}

          {sent ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px',
              background: 'rgba(90,158,114,0.1)',
              border: '1px solid rgba(90,158,114,0.25)',
              borderRadius: '8px',
              fontSize: '12px',
              color: 'var(--ok2)',
              marginBottom: '20px',
            }}>
              <IconCheck className="w-3.5 h-3.5" /> {t('auth.verifyResent')}
            </div>
          ) : (
            !editingEmail && (
              <button
                onClick={resend}
                disabled={loading}
                style={{
                  width: '100%',
                  background: 'var(--acc)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: loading ? 'default' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  marginBottom: '20px',
                  letterSpacing: '0.04em',
                }}>
                {loading ? t('auth.sending') : t('auth.resendVerify')}
              </button>
            )
          )}

          <div style={{ height: '1px', background: 'var(--border)', margin: '0 0 16px' }} />

          <button
            onClick={logout}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--ink3)',
              fontSize: '11px',
              cursor: 'pointer',
              padding: 0,
            }}
            onMouseEnter={e => e.target.style.color = 'var(--err2)'}
            onMouseLeave={e => e.target.style.color = 'var(--ink3)'}
          >
            {t('app.logout')}
          </button>
        </div>

      </div>
    </div>
  );
}
